import PropTypes from "prop-types";
import React, { useContext, useMemo, useRef, useState } from "react";

import { AppSettingsContext } from "@swa-ui/application";
import { useDeviceInfo, useRestoreFocus, window } from "@swa-ui/browser";
import {
  BackgroundVeil,
  Button,
  Caption,
  HorizontalList,
  Icon,
  Image,
  Link,
  PageGrid,
} from "@swa-ui/core";
import { keyCodes } from "@swa-ui/core/defines";
import { HtmlValue, PlacementFactory, usePlacement } from "@swa-ui/placements";
import { classNames } from "@swa-ui/string";

import { navigationPlacementId, notificationCenterAction } from "../../defines/navigationItems";
import { NotificationCenter } from "../../NotificationCenter";
import { SearchAutocomplete } from "../../SearchAutocomplete";
import styles from "./DotcomHeaderDesktop.module.scss";
import { Overlay } from "./Overlay";

/**
 * DotcomHeaderControl provides the header for larger devices / screens.
 */
const CARET_CENTER = 6;
const DELAY_FOR_BROWSER = 100;
const MENU_SELECTION_NONE = -1;
const SEARCH_ACTION = "GLOBALNAVSEARCH";

export const DotcomHeaderDesktop = (props) => {
  const {
    control,
    light,
    menuItemSeparator,
    newNotificationCount,
    onViewNotifications,
    onSearch,
    shouldShowSystemMessagesInNavigation,
    uppercaseMenuItems,
  } = props;
  const { appSettings } = useContext(AppSettingsContext);
  const { isSmallOrMedium, isLargeOrXlarge } = useDeviceInfo();
  const { restoreFocusElement, saveFocusElement } = useRestoreFocus();
  const { placement: globalNavHeader } = usePlacement(navigationPlacementId);
  const [selection, setSelection] = useState({
    currentSelection: MENU_SELECTION_NONE,
    nextSelection: MENU_SELECTION_NONE,
  });
  const menuNavRef = useRef();
  const menuItemRefs = useRef([]);
  const globalNavPrimaryLinks = Array.isArray(globalNavHeader?.globalNavPrimaryLinks)
    ? globalNavHeader.globalNavPrimaryLinks
    : [];
  const filteredGlobalNavPrimaryLinks = useMemo(
    () => globalNavPrimaryLinks.filter(shouldRenderNavItem),
    [globalNavPrimaryLinks, appSettings]
  );
  const [notificationCenterIconBadge, setNotificationCenterIconBadge] =
    useState(newNotificationCount);

  return globalNavHeader ? renderGlobalNavHeader() : null;

  function renderGlobalNavHeader() {
    return isSmallOrMedium ? (
      <>
        <div className={styles.topBar}>
          <div className={styles.logoContainer}>{renderLogo()}</div>
          {renderControl()}
        </div>
        {renderMenuBarSection()}
      </>
    ) : (
      <PageGrid className={styles.layout}>
        <div className={styles.container}>
          {renderControl()}
          <div className={styles.menubar}>
            {renderLogo()}
            {renderMenuBarSection()}
          </div>
        </div>
      </PageGrid>
    );
  }

  function renderLogo() {
    return (
      <Button {...getLogoButtonProps()}>
        <Image {...getLogoImageProps()} />
      </Button>
    );
  }

  function renderControl() {
    return control;
  }

  function renderMenuBarSection() {
    return renderMenu();
  }

  function renderMenu() {
    return (
      <>
        <BackgroundVeil {...getVeilProps()} />
        <Caption {...getCaptionProps()}>
          {isSmallOrMedium ? (
            <div
              className={classNames(styles.horizontalListMenu, {
                [styles.darkBackground]: light,
              })}
            >
              <HorizontalList {...getHorizontalListProps()} />
            </div>
          ) : (
            <div className={styles.menu} ref={menuNavRef} role="navigation">
              {filteredGlobalNavPrimaryLinks.length > 0 &&
                filteredGlobalNavPrimaryLinks.map(renderNavPrimaryLink)}
            </div>
          )}
        </Caption>
      </>
    );
  }

  function renderNavPrimaryLink(menuItem, index) {
    return (
      <span
        key={menuItem.value ?? index}
        ref={(element) => (menuItemRefs.current[index] = element)}
      >
        {renderMainMenuItem(menuItem, index)}
      </span>
    );
  }

  function renderMainMenuItem(menuItem, menuItemIndex) {
    const item = getMenuItem(menuItem);

    return (
      <>
        {!isIconMenuItem(menuItem) && menuItemSeparator && menuItemIndex !== 0 && (
          <span className={styles.menuItemSeparator}>{menuItemSeparator}</span>
        )}
        {item && (
          <Link {...getMainMenuItemProps(menuItem, menuItemIndex, isIconMenuItem(menuItem))}>
            {item}
          </Link>
        )}
      </>
    );
  }

  function renderOverlay() {
    const { currentSelection } = selection;
    const overlayContent = filteredGlobalNavPrimaryLinks?.[currentSelection];
    let overlay;

    if (overlayContent?.action === SEARCH_ACTION) {
      overlay = <SearchAutocomplete className={styles.search} onSearch={handleSearch} />;
    } else if (overlayContent?.action === notificationCenterAction) {
      overlay = <NotificationCenter />;
    } else if (overlayContent?.flyout?.type === "placement") {
      overlay = <PlacementFactory id={overlayContent.flyout.id} shouldWait={false} />;
    } else {
      overlay = <Overlay productIndex={currentSelection} />;
    }

    return overlay;
  }

  function shouldRenderNavItem({ action }) {
    return (
      !action ||
      (action === SEARCH_ACTION && appSettings.searchAvailable) ||
      (action === notificationCenterAction && shouldShowSystemMessagesInNavigation)
    );
  }

  function getLogoButtonProps() {
    const { altText, href, url } = globalNavHeader?.globalNavLogo ?? {};

    return {
      "aria-label": altText,
      className: getLogoClass(),
      href: href ?? url ?? "/",
      styleType: "no-style",
    };
  }

  function getLogoImageProps() {
    const {
      altText: alt,
      content,
      height,
      src: logoSrc,
      width,
    } = globalNavHeader?.globalNavLogo ?? {};
    const { currentSelection } = selection;

    const src =
      light || currentSelection !== MENU_SELECTION_NONE ? getLightLogoSrc(logoSrc) : logoSrc;

    return {
      alt,
      content,
      height,
      role: "img",
      src,
      width,
    };
  }

  function getLightLogoSrc(logoType) {
    return logoType.replace("dark", "light");
  }

  function getVeilProps() {
    const { currentSelection } = selection;

    return {
      on: currentSelection !== MENU_SELECTION_NONE,
      onClick: handleVeilClick,
      onKeyDown: handleVeilKeyDown,
    };
  }

  function getCaptionProps() {
    const { currentSelection, nextSelection } = selection;
    const location =
      currentSelection === MENU_SELECTION_NONE || currentSelection !== nextSelection
        ? "hidden"
        : "below";

    return {
      adjoiningContent: currentSelection !== MENU_SELECTION_NONE && renderOverlay(),
      alignment: "right",
      bestFit: false,
      className: getCaptionClass(),
      id: `header${currentSelection}-${nextSelection}`,
      location,
      mainClassName: styles.mainContent,
      onTransformationEnd: handleShowComplete,
      pointerAlignment: "right",
      pointerXOffset: getPointerXOffset(),
      role: "navigation",
    };
  }

  function getHorizontalListProps() {
    return {
      className: styles.horizontalList,
      items: getHorizontalListItems(),
      light: light,
      separator: false,
      spacing: "space-between",
      tagName: "link",
    };
  }

  function getHorizontalListItems() {
    return mapMenuItems((menuItem, index) => {
      const item = getMenuItem(menuItem);
      const menuItemProps = getMainMenuItemProps(menuItem, index, isIconMenuItem(menuItem));

      return item
        ? {
            componentProps: {
              ...menuItemProps,
              children: item,
            },
          }
        : null;
    }).filter(Boolean);
  }

  function getMenuItem(menuItem) {
    let item;

    if (isIconMenuItem(menuItem)) {
      item = <Icon {...getLinkIconProps(menuItem)} />;
    } else {
      item = <HtmlValue htmlValue={menuItem.value} />;
    }

    return item;
  }

  function getMainMenuItemProps(menuItem, menuIndex, iconOnlyItem) {
    const { currentSelection } = selection;
    const { href, url } = menuItem;

    return {
      className: classNames({
        [styles.iconOnlyMenuItem]: isLargeOrXlarge && iconOnlyItem,
        [styles.menuItem]: !iconOnlyItem,
        [styles.menuItemClosed]: !iconOnlyItem && currentSelection === MENU_SELECTION_NONE,
        [styles.menuItemUppercase]: uppercaseMenuItems,
      }),
      emphasis: true,
      href: href ?? url,
      light: light || currentSelection !== MENU_SELECTION_NONE,
      onClick: !href && !url ? handleMenuItemClick.bind(this, menuItem, menuIndex) : undefined,
      paddingForFocus: true,
      size: "fontSize13",
    };
  }

  function getLinkIconProps(menuItem) {
    const { currentSelection } = selection;

    return {
      badge: menuItem.action === notificationCenterAction && notificationCenterIconBadge,
      color:
        currentSelection === MENU_SELECTION_NONE
          ? "cmp-dotcom-color-header-search"
          : "primary-header-search-open",
      name: menuItem.prefixIcon,
      size: "size22",
    };
  }

  function getPointerXOffset() {
    const { currentSelection } = selection;
    let xOffset = 0;

    if (menuItemRefs.current[currentSelection]) {
      const navPosition = menuNavRef.current.getBoundingClientRect();
      const menuOptionPosition = menuItemRefs.current[currentSelection].getBoundingClientRect();
      const xPosition = navPosition.right - menuOptionPosition.x;
      const centerPosition = menuOptionPosition.width / 2;

      xOffset = -xPosition + centerPosition + CARET_CENTER + CARET_CENTER;
    }

    return xOffset;
  }

  function getLogoClass() {
    const { currentSelection } = selection;

    return classNames({
      [styles.logo]: true,
      [styles.priorityStacking]: currentSelection !== MENU_SELECTION_NONE,
    });
  }

  function getCaptionClass() {
    const { currentSelection } = selection;

    return classNames({
      [styles.caption]: true,
      [styles.open]: currentSelection !== MENU_SELECTION_NONE,
    });
  }

  function handleVeilClick() {
    restoreFocusElement();
    setSelection({
      currentSelection: selection.currentSelection,
      nextSelection: MENU_SELECTION_NONE,
    });
  }

  function handleVeilKeyDown(event) {
    if (event.key === keyCodes.KEY_ESCAPE) {
      restoreFocusElement();
      setSelection({
        currentSelection: selection.currentSelection,
        nextSelection: MENU_SELECTION_NONE,
      });
    }
  }

  function handleMenuItemClick(_menuItem, menuItemIndex) {
    const { currentSelection } = selection;
    let newNextSelection = menuItemIndex;

    if (currentSelection !== MENU_SELECTION_NONE) {
      newNextSelection =
        currentSelection === newNextSelection ? MENU_SELECTION_NONE : menuItemIndex;
    }

    if (filteredGlobalNavPrimaryLinks[menuItemIndex].action === notificationCenterAction) {
      if (typeof onViewNotifications === "function") {
        onViewNotifications();
      }

      setNotificationCenterIconBadge(0);
    }

    saveFocusElement();

    setSelection({
      currentSelection,
      nextSelection: newNextSelection,
    });
  }

  function handleShowComplete() {
    const { currentSelection, nextSelection } = selection;

    if (currentSelection !== nextSelection) {
      window.setTimeout(() => {
        setSelection({
          currentSelection: nextSelection,
          nextSelection: nextSelection,
        });
      }, DELAY_FOR_BROWSER);
    }
  }

  function handleSearch(searchCriteria) {
    setSelection({
      currentSelection: selection.currentSelection,
      nextSelection: MENU_SELECTION_NONE,
    });
    onSearch && onSearch(searchCriteria);
  }

  function mapMenuItems(menuItem) {
    return filteredGlobalNavPrimaryLinks.map(menuItem) || [];
  }

  function isIconMenuItem({ action } = {}) {
    return action === SEARCH_ACTION || action === notificationCenterAction;
  }
};

DotcomHeaderDesktop.displayName = "DotcomHeaderDesktop";
DotcomHeaderDesktop.propTypes = {
  /** Header control used for items like  account and locale for the header. */
  control: PropTypes.node,

  /** Indicates whether the app is using a dark Header background, to be passed to elements for styling. */
  light: PropTypes.bool,

  /** Adds the value of the prop as a separator between the menu links. */
  menuItemSeparator: PropTypes.string,

  /** The number of new notifications for the user */
  newNotificationCount: PropTypes.number,

  /** Calllback that will be used when traveler performs a search. */
  onSearch: PropTypes.func,

  /** Callback that will be used when the notification center is viewed */
  onViewNotifications: PropTypes.func,

  /** Transform menu item links into uppercase. Default: true */
  uppercaseMenuItems: PropTypes.bool,
};

DotcomHeaderDesktop.defaultProps = {
  uppercaseMenuItems: true,
};
